<template>
  <div class="content">
    <div
      v-if="statusMessage && !statusDismissed"
      class="alert alert-dismissible fade show"
      :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
      role="alert"
    >
      {{ statusMessage }}
      <button
        type="button"
        class="btn-close"
        aria-label="Close"
        @click="dismissStatus"
      ></button>
    </div>
    <!-- Tabela z listą użytkowników (tylko jeśli nie jesteśmy w trybie edycji) -->
    <table class="user-table table table-striped" v-if="!edit">
      <thead class="thead-light">
        <tr>
          <!-- Nagłówki kolumn -->
          <th style="width: 2%">{{ t('ordinal-number') }}</th>
          <th style="width: 10%">{{ t('name2') }}</th>
          <th style="width: 10%">{{ t('permissions') }}</th>
          <th style="width: 15%">{{ t('last-login') }}</th>
          <th style="width: 10%">{{ t('name') }}</th>
          <th style="width: 15%">{{ t('lastname') }}</th>
          <th style="width: 20%">{{ t('email') }}</th>
          <th style="width: 10%">{{ t('status') }}</th>
          <th style="width: 5%">{{ t('action') }}</th>
          <th style="width: 3%"></th>
        </tr>
      </thead>
      <tbody>
        <!-- Pętla po użytkownikach wyświetlanych na danej stronie -->
        <tr v-for="users in paginatedUsers" :key="users.id" class="users-list">
          <td>{{ users.id }}</td>
          <td>{{ users.username }}</td>
          <td>
            <!-- Zwraca uprawnienia użytkownika (role jako tekst) -->
            {{ users.roles ? users.roles.map((it) => it.name).join(" ") : "" }}
          </td>
          <td>
            <!-- Data ostatniego logowania, sformatowana -->
            {{ users.lastLogin ? moment(users.lastLogin).format("YYYY-MM-DD HH:mm:ss") : "" }}
          </td>
          <td>{{ users.firstName }}</td>
          <td>{{ users.lastName }}</td>
          <td>{{ users.email }}</td>
          <td>
            <!-- Status aktywności -->
            {{ users.enabled ? "aktywne" : "nieaktywne" }}
          </td>
          <td>
            <!-- Przycisk do przejścia w tryb edycji wybranego użytkownika -->
            <button
              type="button"
              @click="setToEdit(users.id)"
              class="btn button-edit"
            >
              {{ t('edit') }}
            </button>
          </td>
          <td>
            <!-- Checkbox do zaznaczania użytkownika do usunięcia -->
            <input-checkbox
              @click="setToDelete(users.id)"
              :checked-condition="toDelete.includes(users.id)"
            ></input-checkbox>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- Panel paginacji i przycisk usuwania zaznaczonych użytkowników (gdy nie w trybie edycji) -->
    <div class="actions" v-if="!edit">
      <PagePanel
        :page-num="pageNumber"
        :page-size="pageSize"
        :total-size="totalCount"
        v-on:setPage="setPage"
      />
      <button
        type="button"
        @click="deleteUsers"
        :disabled="toDelete.length === 0"
        class="remove btn button-delete"
      >
        {{ t('delete') }}
      </button>
    </div>
    <!-- Komponent formularza edycji użytkownika (po kliknięciu przycisku edytuj) -->
    <div class="content" v-if="edit">
      <EditUser :user-id="toEdit" v-on:goBack="handleGoBack" />
    </div>
  </div>
</template>

<script>
// Importujemy narzędzia i komponenty
import moment from "moment"; // Do formatowania dat
import Users from "../services/Users"; // Serwis pobierania użytkowników z backendu
import InputCheckbox from "@/components/InputCheckbox"; // Własny komponent checkboxa
import EditUser from "@/components/EditUser"; // Komponent do edycji użytkownika
import PagePanel from "@/components/table/PagePanel"; // Komponent paginacji

moment.locale("pl"); // Ustawienie polskiego locale

export default {
  components: { PagePanel, EditUser, InputCheckbox }, // Deklarujemy używane komponenty
  name: "UserList",
  data() {
    return {
      loading: false,         // Czy trwa ładowanie użytkowników
      users: [],              // Lista użytkowników z backendu (wszystkich)
      filteredUsers: [],      // Lista użytkowników po filtracji (np. po wyszukiwaniu)
      pageNumber: 1,          // Numer bieżącej strony (dla paginacji)
      pageSize: 10,           // Liczba użytkowników na stronie
      totalCount: 10,         // Łączna liczba użytkowników
      error: null,            // Pole na błąd
      moment: moment,         // Instancja moment.js
      dataService: Users,     // Serwis komunikacji z backendem
      toDelete: [],           // Tablica ID użytkowników zaznaczonych do usunięcia
      edit: false,            // Czy jesteśmy w trybie edycji użytkownika
      toEdit: null,           // ID użytkownika aktualnie edytowanego
      statusMessage: "",
      statusType: "error",
      statusDismissed: false,
    };
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },
  watch: {
    // Reaguje na zmianę zapytania wyszukiwania
    searchQuery(newQuery) {
      this.filterUsers(newQuery);
    },
  },
  created() {
    // Automatycznie pobierz listę użytkowników po załadowaniu komponentu
    this.retrieveUsers();
  },
  computed: {
    // Zwraca użytkowników wyświetlanych na bieżącej stronie (paginacja)
    paginatedUsers() {
      const startIndex = (this.pageNumber - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredUsers.slice(startIndex, endIndex);
    },
  },
  methods: {
    // Filtrowanie użytkowników po nazwie użytkownika (case-insensitive)
    filterUsers(query) {
      if (!query) {
        this.filteredUsers = this.users;
      } else {
        const lowerCaseQuery = query.toLowerCase();
        this.filteredUsers = this.users.filter((user) =>
          user.username.toLowerCase().includes(lowerCaseQuery)
        );
      }
      this.pageNumber = 1; // Po filtracji wracamy do pierwszej strony
      this.$forceUpdate(); // Wymuszamy odświeżenie komponentu
    },
    // Powrót z widoku edycji użytkownika do listy
    handleGoBack() {
      this.edit = false;
      this.toEdit = null;
      this.$emit('editMode', false);
      this.retrieveUsers(); // Po powrocie odśwież listę użytkowników
    },
    // Ustaw numer strony (z paginacji)
    setPage(event) {
      this.pageNumber = event;
      this.refreshData();
    },
    // Pobierz użytkowników z backendu (przy ładowaniu komponentu lub po powrocie z edycji)
    retrieveUsers() {
      this.loading = true;
      this.toDelete = [];
      this.toEdit = null;
      this.edit = false;
      this.refreshData();
    },
    // Pobierz i uaktualnij listę użytkowników (np. po zmianie strony)
    refreshData() {
      this.users = [];
      this.dataService
        .getUserList(this.pageNumber, this.pageSize)
        .then((data) => {
          let tmpArray = [];
          if (data.content && data.content.length > 0) {
            tmpArray = data.content;
          }
          this.users = tmpArray;
          this.filteredUsers = tmpArray;
          this.totalCount = data.totalCount;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          this.showStatus("Nie udało się pobrać listy użytkowników.", "error");
        });
    },
    // Przejdź do widoku edycji użytkownika (po kliknięciu "Edytuj")
    setToEdit(id) {
      this.toDelete = [];
      this.edit = true;
      this.toEdit = id;
      this.$emit('editMode', true);
    },
    // Zaznacz lub odznacz użytkownika do usunięcia (checkbox)
    setToDelete(id) {
      if (this.toDelete.includes(id)) {
        this.toDelete.splice(this.toDelete.indexOf(id), 1);
      } else {
        this.toDelete.push(id);
      }
    },
    // Usuń wszystkich zaznaczonych użytkowników (po kliknięciu "Usuń")
    deleteUsers() {
      Users.deleteUsers(this.toDelete)
        .then(() => {
          this.toDelete = [];
          this.$emit("clearSearch"); // Wyczyść zapytanie wyszukiwania (jeśli było)
          this.refreshData();
          this.showStatus("Użytkownicy zostali usunięci.", "success");
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          this.showStatus("Nie udało się usunąć użytkowników.", "error");
        });
    },
    showStatus(message, type = "error") {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },
    dismissStatus() {
      this.statusDismissed = true;
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}
.content > .alert {
  margin: 1rem;
}
.content > .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

h3 {
  padding: 3px 15px;
  background-color: white;
}

.actions {
  position: relative;
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1.75rem;
  padding-top: 0.5rem;
}

.remove {
  position: absolute;
  right: 47px;
  top: 0;
}

.user-table {
  width: 100%;
  font-size: 12px;
  color: black;
}

.users-list td {
  padding-top: 1rem;
}

.users-list td:nth-child(9) {
  padding-top: 0.5rem;
}

.button-edit {
  padding: 0.5rem 1rem 0.5rem 1rem;
} 

.button-edit:hover {
  background-color: #C77501 !important;
  color: white !important;
}

.button-delete {
  padding: 0.5rem 1.20rem 0.5rem 1.20rem;
  margin-top: 0.25rem;
}

.button-delete:hover {
  background-color: #e14544 !important;
  color: white !important;
}

</style>
